"use client";

// Une seule sollicitation à la fois.
//
// La modale de promotion et la carte du changelog vivent dans deux branches
// différentes de l'arbre (UserLayout d'un côté, le provider global de l'autre)
// et s'ignoraient donc totalement : elles apparaissaient ensemble, la remise et
// la nouveauté se disputant le même clic dans le même écran. La promotion passe
// devant, le changelog attend qu'elle ait rendu l'écran.
//
// Le verrou est tenu par jeton plutôt que par un simple booléen : une page
// comme /mentoring monte deux UserLayout imbriqués, et le second ne doit pas
// pouvoir lever le verrou posé par le premier.

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useId,
    useMemo,
    useState,
} from "react";

const PromotionModalContext = createContext({
    isShowing: false,
    hold: () => {},
    release: () => {},
});

export function PromotionModalProvider({ children }) {
    const [holders, setHolders] = useState(() => new Set());

    const hold = useCallback((token) => {
        setHolders((previous) => {
            if (previous.has(token)) return previous;
            const next = new Set(previous);
            next.add(token);
            return next;
        });
    }, []);

    const release = useCallback((token) => {
        setHolders((previous) => {
            if (!previous.has(token)) return previous;
            const next = new Set(previous);
            next.delete(token);
            return next;
        });
    }, []);

    const value = useMemo(
        () => ({ isShowing: holders.size > 0, hold, release }),
        [holders, hold, release],
    );

    return (
        <PromotionModalContext.Provider value={value}>
            {children}
        </PromotionModalContext.Provider>
    );
}

/**
 * Réserve l'écran pour la modale de promotion tant que `active` est vrai.
 *
 * Le jeton est propre à l'instance appelante et rendu au démontage : un
 * composant qui disparaît ne laisse jamais le verrou posé derrière lui.
 */
export function usePromotionModalHold(active) {
    const { hold, release } = useContext(PromotionModalContext);
    const token = useId();

    useEffect(() => {
        if (!active) return;
        hold(token);
        return () => release(token);
    }, [active, hold, release, token]);
}

/**
 * @returns {boolean} vrai tant que la modale de promotion occupe l'écran ou
 * peut encore y surgir d'un instant à l'autre (verdict du serveur en attente).
 */
export function usePromotionModalIsShowing() {
    return useContext(PromotionModalContext).isShowing;
}
